/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import PropTypes from "prop-types";
import { CustomLink } from "../../custom-link";
import format from "date-fns/format";
import { SvgHandler } from "../../svg-handler/svg-handler";
import defaultAuthorIcon from "../../../../assets/icons/default-author.svg";
import envelope from "../../../../assets/icons/envelope.svg";

import "./user-profile.m.css";

const ProfileSkeleton = () => (
  <div styleName="container-wrapper animation">
    <div styleName="load-style">
      <div styleName="load-img" />
    </div>
    <div>
      <div styleName="load-author-name" />
      <div styleName="load-subs-details" />
      <div styleName="load-line" />
      <div styleName="load-email-detail">
        <span styleName="load-email" />
        <p styleName="load-detail" />
      </div>
      <div styleName="load-mob-detail">
        <span styleName="load-mobile" />
        <p styleName="load-detail" />
      </div>
      <div styleName="load-plan-btn" />
    </div>
  </div>
);

const getActivePlan = subscriptions => {
  return subscriptions.find(plan => plan.active && plan["subscription_type"] === "standard") || {};
};

const UserAvatar = ({ avatarUrl }) => {
  if (avatarUrl) {
    return <img src={avatarUrl} loading="lazy" />;
  }
  return <SvgHandler xlinkHref={defaultAuthorIcon} width="125" height="125" viewBox="0 0 125px 125px" />;
};

UserAvatar.propTypes = {
  avatarUrl: PropTypes.string
};

const UserSubscriptionDetails = ({ subscriptions }) => {
  if (!subscriptions) return null;

  const { end_timestamp: endTimestamp, active: isActive, plan_name: planName } = getActivePlan(subscriptions);

  return isActive ? (
    <div styleName="subs-details" data-testid="subs-details">
      <div styleName="plan-details">Active Plan</div>
      <div styleName="plan-details">
        <span styleName="plan-name">{planName}</span>
        <span styleName="expire-time">
          (Expiry &nbsp;
          {format(endTimestamp, "DD MMM, YYYY")})
        </span>
      </div>
    </div>
  ) : (
    <span styleName="subs-details">No subscriptions yet</span>
  );
};

UserSubscriptionDetails.propTypes = {
  subscriptions: PropTypes.array
};

const UserProfile = ({ enableAccesstype, member, subscriptions, isSubscriptionsLoading }) => {
  if (isSubscriptionsLoading) return <ProfileSkeleton />;
  return (
    <div styleName="container-wrapper">
      <div styleName="author-image">
        <UserAvatar avatarUrl={member["avatar-url"]} />
      </div>
      <div styleName="author-details">
        <h2 styleName="author-name">{member.name}</h2>
        <div styleName="email-detail">
          <span styleName="envelope">
            <SvgHandler xlinkHref={envelope} width="24" height="24" viewBox="0 0 24px 24px" />
          </span>
          <p>{member.email}</p>
        </div>
        {!member["phone-number"] ? null : (
          <div styleName="mob-detail">
            <span>PHONE</span>
            <p>{member["phone-number"]}</p>
          </div>
        )}
        {enableAccesstype && (
          <div styleName="subscription-detail">
            {!isSubscriptionsLoading && <UserSubscriptionDetails subscriptions={subscriptions} />}
            <div styleName="other-detail">
              <div data-testid="plan-btn" styleName="plan-btn">
                <CustomLink href={"/subscription"}>Upgrade Plan</CustomLink>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

UserProfile.propTypes = {
  isAccessTypeLoaded: PropTypes.bool,
  enableAccesstype: PropTypes.bool,
  isSubscriptionsLoading: PropTypes.bool,
  member: PropTypes.shape({
    name: PropTypes.string,
    "avatar-url": PropTypes.string,
    email: PropTypes.string,
    "phone-number": PropTypes.string
  }),
  subscriptions: PropTypes.array
};

export { UserProfile };
